import React, { useEffect, useState } from 'react';
import {
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Typography,
} from '@material-ui/core';
import {
  ChipSelectionType,
  ProbableProductCandidate,
  ProbableProductSignal,
  ProbableProductsResponse,
  ReceiptDetailResponse,
} from '../../models';
import { getUnknownIdentifierReceiptDetail } from '../../api';
import { checkStatus, handleError, parseJSON } from '../../../shared/api/core/common';

const SIGNAL_LABELS: Record<string, string> = {
  pos_search_add: 'POS Search',
  receipt_index: 'Receipt Index',
  cross_receipt: 'Cross Receipt',
  code_group: 'Code Group',
};

type ProbableMatchesDialogProps = {
  open: boolean;
  loading: boolean;
  data: ProbableProductsResponse | null;
  selectedUnknownCode: string | null;
  businessId: number | string | undefined;
  onClose: () => void;
  getConfidenceClass: (confidence: string) => string;
  evidenceBoxClass: string;
  candidateRowClass: string;
  signalChipClass: string;
};

const ProbableMatchesDialog: React.FC<ProbableMatchesDialogProps> = ({
  open,
  loading,
  data,
  selectedUnknownCode,
  businessId,
  onClose,
  getConfidenceClass,
  evidenceBoxClass,
  candidateRowClass,
  signalChipClass,
}) => {
  const [expandedChip, setExpandedChip] = useState<{
    productUuid: string;
    chipType: ChipSelectionType;
  } | null>(null);
  const [receiptDetailOpen, setReceiptDetailOpen] = useState(false);
  const [receiptDetailLoading, setReceiptDetailLoading] = useState(false);
  const [receiptDetail, setReceiptDetail] = useState<ReceiptDetailResponse | null>(null);

  useEffect(() => {
    if (!open) {
      setExpandedChip(null);
      setReceiptDetailOpen(false);
      setReceiptDetail(null);
    }
  }, [open]);

  const handleChipClick = (
    productUuid: string,
    chipType: ChipSelectionType,
    event: React.MouseEvent,
  ) => {
    event.stopPropagation();
    const isSame =
      expandedChip?.productUuid === productUuid && expandedChip?.chipType === chipType;
    setExpandedChip(isSame ? null : { productUuid, chipType });
  };

  const handleViewReceipt = (
    historyId: number,
    productUuid?: string,
  ) => {
    if (!businessId || !selectedUnknownCode) {
      return;
    }
    setReceiptDetailOpen(true);
    setReceiptDetailLoading(true);
    setReceiptDetail(null);

    getUnknownIdentifierReceiptDetail(
      businessId,
      historyId,
      selectedUnknownCode,
      productUuid,
    )
      .catch(handleError)
      .then(checkStatus)
      .then(parseJSON)
      .then((response: ReceiptDetailResponse) => setReceiptDetail(response))
      .catch(() => setReceiptDetailOpen(false))
      .finally(() => setReceiptDetailLoading(false));
  };

  const renderReceiptButton = (historyId: number, productUuid?: string) => (
    <Button
      size="small"
      color="primary"
      style={{ marginTop: 4 }}
      onClick={() => handleViewReceipt(historyId, productUuid)}
    >
      View receipt
    </Button>
  );

  const renderConfidenceEvidence = (candidate: ProbableProductCandidate) => (
    <div className={evidenceBoxClass}>
      <Typography variant="body2">{candidate.confidence_reason}</Typography>
      <Typography variant="caption" color="textSecondary" display="block" style={{ marginTop: 4 }}>
        Driving signal: {SIGNAL_LABELS[candidate.confidence_driving_signal] || candidate.confidence_driving_signal}
      </Typography>
    </div>
  );

  const renderSignalEvidence = (
    candidate: ProbableProductCandidate,
    signal: ProbableProductSignal,
  ) => {
    const evidence = candidate.signal_evidence || {};

    if (signal === 'pos_search_add') {
      const rows = evidence.pos_search_add || [];
      if (rows.length === 0) {
        return (
          <Typography variant="body2" color="textSecondary">
            No POS search/add tracking linked to this product.
          </Typography>
        );
      }
      return rows.map((row) => (
        <div key={`pos-${row.history_id}-${row.added_at}`} style={{ marginBottom: 8 }}>
          <Typography variant="body2">
            Receipt {row.receipt_number || '—'} | scan index {row.scan_index} | searched &quot;
            {row.search_term}&quot; → added {row.product_number} — {row.product_name}
          </Typography>
          {row.added_at && (
            <Typography variant="caption" color="textSecondary">
              Added at {row.added_at}
            </Typography>
          )}
          {renderReceiptButton(row.history_id, candidate.product_uuid)}
        </div>
      ));
    }

    if (signal === 'receipt_index') {
      const rows = evidence.receipt_index || [];
      if (rows.length === 0) {
        return (
          <Typography variant="body2" color="textSecondary">
            Not a receipt-index candidate on any occurrence.
          </Typography>
        );
      }
      return rows.map((row) => (
        <div key={`ri-${row.history_id}-${row.line_index}`} style={{ marginBottom: 8 }}>
          <Typography variant="body2">
            Receipt {row.receipt_number || '—'} | scan index {row.scan_index} | line{' '}
            {row.line_index} | recognition {row.recognition_number || '—'}
          </Typography>
          <Typography variant="caption" color="textSecondary">
            {row.included_reason} (source: {row.receipt_source})
          </Typography>
          {renderReceiptButton(row.history_id, candidate.product_uuid)}
        </div>
      ));
    }

    if (signal === 'cross_receipt') {
      const cross = evidence.cross_receipt;
      if (!cross) {
        return (
          <Typography variant="body2" color="textSecondary">
            Cross-receipt signal not applied for this product.
          </Typography>
        );
      }
      return (
        <>
          <Typography variant="body2">
            Present on {cross.matching_occurrences} of {cross.total_occurrences} occurrence(s).
          </Typography>
          {cross.matching_history_ids.length > 0 && (
            <Typography variant="caption" color="textSecondary" display="block">
              Matching history IDs: {cross.matching_history_ids.join(', ')}
            </Typography>
          )}
          {cross.excluded_history_ids.length > 0 && (
            <Typography variant="caption" color="textSecondary" display="block">
              Excluded history IDs: {cross.excluded_history_ids.slice(0, 20).join(', ')}
              {cross.excluded_history_ids.length > 20 ? '…' : ''}
            </Typography>
          )}
        </>
      );
    }

    if (signal === 'code_group') {
      const codeGroup = evidence.code_group;
      if (!codeGroup) {
        return (
          <Typography variant="body2" color="textSecondary">
            No code group evidence for this product.
          </Typography>
        );
      }
      return (
        <>
          <Typography variant="body2">
            Code group {codeGroup.code_block_start} – {codeGroup.code_block_end} (id{' '}
            {codeGroup.code_group_id})
          </Typography>
          <Typography variant="caption" color="textSecondary" display="block">
            Brand: {codeGroup.brand_name || '—'} | Company: {codeGroup.company_name || '—'}
          </Typography>
          <Typography variant="caption" color="textSecondary" display="block">
            Master product: {codeGroup.master_product_name || '—'} (id{' '}
            {codeGroup.master_product_id ?? '—'}) | Link: {codeGroup.link_status}
          </Typography>
        </>
      );
    }

    return null;
  };

  const renderExpandedEvidence = (candidate: ProbableProductCandidate) => {
    if (!expandedChip || expandedChip.productUuid !== candidate.product_uuid) {
      return null;
    }
    const { chipType } = expandedChip;
    const title =
      chipType === 'confidence'
        ? `Why ${candidate.confidence}?`
        : SIGNAL_LABELS[chipType] || chipType;

    return (
      <div className={evidenceBoxClass} style={{ marginTop: 8 }}>
        <Typography variant="subtitle2" gutterBottom>
          {title}
        </Typography>
        {chipType === 'confidence'
          ? renderConfidenceEvidence(candidate)
          : renderSignalEvidence(candidate, chipType as ProbableProductSignal)}
      </div>
    );
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>
          Probable Matches
          {selectedUnknownCode ? ` — ${selectedUnknownCode}` : ''}
        </DialogTitle>
        <DialogContent dividers>
          {loading && (
            <div style={{ display: 'flex', justifyContent: 'center', padding: 24 }}>
              <CircularProgress />
            </div>
          )}
          {!loading && data && (
            <>
              <Typography variant="body2" gutterBottom>
                {data.occurrence_count} occurrence(s) found for this code.
              </Typography>
              {data.candidates.length === 0 && (
                <Typography variant="body1" style={{ marginTop: 16 }}>
                  No probable product matches were found.
                </Typography>
              )}
              {data.candidates.map((candidate) => (
                <div key={candidate.product_uuid} className={candidateRowClass}>
                  <Typography variant="subtitle1">
                    {candidate.product_number} — {candidate.product_name}
                  </Typography>
                  <div style={{ marginTop: 8 }}>
                    <Chip
                      size="small"
                      label={candidate.confidence}
                      className={getConfidenceClass(candidate.confidence)}
                      clickable
                      onClick={(event) => handleChipClick(candidate.product_uuid, 'confidence', event)}
                      color={
                        expandedChip?.productUuid === candidate.product_uuid &&
                        expandedChip?.chipType === 'confidence'
                          ? 'primary'
                          : undefined
                      }
                    />
                    {candidate.signals.map((signal) => (
                      <Chip
                        key={`${candidate.product_uuid}-${signal}`}
                        size="small"
                        label={SIGNAL_LABELS[signal] || signal}
                        className={signalChipClass}
                        clickable
                        onClick={(event) => handleChipClick(candidate.product_uuid, signal, event)}
                        color={
                          expandedChip?.productUuid === candidate.product_uuid &&
                          expandedChip?.chipType === signal
                            ? 'primary'
                            : undefined
                        }
                      />
                    ))}
                  </div>
                  <Typography variant="caption" color="textSecondary">
                    Seen in {candidate.receipt_occurrence_count} receipt occurrence(s)
                  </Typography>
                  {renderExpandedEvidence(candidate)}
                </div>
              ))}
              {data.diagnostics.length > 0 && (
                <>
                  <Divider style={{ margin: '16px 0' }} />
                  <Typography variant="subtitle2">Diagnostics</Typography>
                  {data.diagnostics.map((diagnostic) => (
                    <div
                      key={`${diagnostic.code}-${diagnostic.message}`}
                      className={evidenceBoxClass}
                    >
                      <Typography variant="body2">{diagnostic.message}</Typography>
                      {diagnostic.fix_hint && (
                        <Typography variant="caption" color="textSecondary">
                          {diagnostic.fix_hint}
                        </Typography>
                      )}
                    </div>
                  ))}
                </>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={receiptDetailOpen}
        onClose={() => setReceiptDetailOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Receipt detail
          {receiptDetail?.receipt_number ? ` — ${receiptDetail.receipt_number}` : ''}
        </DialogTitle>
        <DialogContent dividers>
          {receiptDetailLoading && (
            <div style={{ display: 'flex', justifyContent: 'center', padding: 24 }}>
              <CircularProgress />
            </div>
          )}
          {!receiptDetailLoading && receiptDetail && (
            <>
              <Typography variant="body2" gutterBottom>
                Unknown scan at line index {receiptDetail.scan_index} | source:{' '}
                {receiptDetail.receipt_source}
                {receiptDetail.receipt_not_synced ? ' (not synced)' : ''}
              </Typography>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
                  <thead>
                    <tr>
                      <th align="left">#</th>
                      <th align="left">Product</th>
                      <th align="left">Recognition</th>
                      <th align="left">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {receiptDetail.lines.map((line) => (
                      <tr
                        key={line.line_index}
                        style={{
                          backgroundColor: line.is_highlighted_product
                            ? '#e3f2fd'
                            : line.is_scan_point
                              ? '#fff3e0'
                              : line.is_receipt_index_candidate
                                ? '#f1f8e9'
                                : undefined,
                        }}
                      >
                        <td>{line.line_index}</td>
                        <td>
                          {line.product_number
                            ? `${line.product_number} — ${line.product_name}`
                            : line.product_name || '—'}
                        </td>
                        <td>{line.recognition_number || '—'}</td>
                        <td>{line.line_status}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReceiptDetailOpen(false)} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ProbableMatchesDialog;
